import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  Alert,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useSelector } from "react-redux";
import { useFetch } from "../../hooks";
import { BASE_URL_TEST } from "../../utils/baseUrl";
import { urls } from "../../urls/urls";
import { Stepper, Step, StepLabel } from "@mui/material";
import {
  GoogleMap,
  MarkerF,
  useJsApiLoader,
  DirectionsRenderer,
} from "@react-google-maps/api";
import jsPDF from "jspdf";
import { convertTimeStampToDate } from "../../utils/DateFormats";

const OrderCard = ({
  order,
  handleOpenInvoiceModal,
  handleOpenRatingModal,
}) => {
  if (!order) return null;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", xl: 1800 },
        display: "flex",
        flexDirection: "column",
        p: 0,
      }}
    >
      <Box
        sx={{
          background: "#4CAF50",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          Order ID: {order.cart_number}
        </Typography>
        <Typography variant="body1">Delivered</Typography>
      </Box>

      <CardContent>
        {order.vendors?.map((vendor) => (
          <Box key={vendor.cart_vendor_id} mb={3}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography fontWeight={700}>
                Vendor Order ID: {vendor.cart_number}
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                Transaction Date:{" "}
                {order?.transaction_date_time
                  ? convertTimeStampToDate(order.transaction_date_time)
                  : "--"}
              </Typography>
              <Typography fontWeight={700}>
                Distance: {vendor?.distance ?? "--"}
              </Typography>
            </Box>

            {vendor.items?.map((item) => (
              <Box
                key={item.cart_item_id}
                display="flex"
                alignItems="center"
                gap={3}
                mb={2}
              >
                <img
                  src={item.product?.images?.[0]}
                  alt={item.product?.name}
                  style={{ width: 80, height: 80, borderRadius: 10 }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {item.product?.name}
                  </Typography>
                  <Typography variant="body2">Qty: {item.quantity}</Typography>
                  {/* Rate Button */}
                  {/* {item.status !== 1 && ( */}
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      mt: 1,
                      textTransform: "none",
                      color: "#FF9800",
                      borderColor: "#FF9800",
                      "&:hover": {
                        backgroundColor: "rgba(255,152,0,0.08)",
                      },
                    }}
                    onClick={() => handleOpenRatingModal(vendor, item)}
                  >
                    Rate this product
                  </Button>
                  {/* )} */}
                </Box>

                <Box ml="auto" textAlign="right">
                  <Typography variant="h6" fontWeight="bold">
                     <span>₹</span> <span>{item.quantity * item.sellingPrice}</span>
                  </Typography>
                </Box>
              </Box>
            ))}
            <Button
              variant="outlined"
              sx={{
                ml: 2,
                borderRadius: 2,
                textTransform: "none",
                color: "#00897B",
                borderColor: "#00897B",
                "&:hover": {
                  borderColor: "#00796B",
                  backgroundColor: "rgba(0, 137, 123, 0.04)",
                },
              }}
              onClick={() => handleOpenInvoiceModal(vendor)}
            >
              Invoice
            </Button>
          </Box>
        ))}
      </CardContent>

      <Divider sx={{ my: 2 }} />
    </Box>
  );
};

const Delivered = () => {
  const customerId = useSelector((state) => state.user?.customerId);
  const apptoken = useSelector((state) => state.user?.apptoken);
  const [deliveredOrdersList, setDeliveredOrdersList] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [trackOderModal, setTrackOderModal] = useState(false);
  const [viewOrderData, setViewOrderData] = useState(null);
  const [isViewOrderModalOpen, setViewOrderModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [ratingValue, setRatingValue] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [ratingErrorMsg, setRatingErrorMsg] = useState("");
  const [ratingSuccessMsg, setRatingSuccessMsg] = useState("");
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [data, setData] = useState("");

  const {
    data: orderData,
    error: orderError,
    fetchData: fetchOrdersData,
  } = useFetch(BASE_URL_TEST + urls("getOrderByUserId", customerId), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: apptoken ? `Bearer ${apptoken}` : "",
    },
  });

  const {
    data: ratingOrderData,
    isLoading: ratingOrderLoading,
    error: ratingOrderError,
    fetchData: fetchRatingOrder,
  } = useFetch(BASE_URL_TEST + urls("submitRating"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: apptoken ? `Bearer ${apptoken}` : "",
    },
  });

  const {
    data: siteSettingsData,
    isLoading: isSiteSettingsDataLoading,
    error: siteSettingsError,
    fetchData: fetchSiteSettingsData,
  } = useFetch(BASE_URL_TEST + urls("getAdminSiteSettings"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: apptoken ? `Bearer ${apptoken}` : "",
    },
  });

  useEffect(() => {
    if (customerId) {
      fetchOrdersData();
      fetchSiteSettingsData();
    }
  }, [customerId]);

  useEffect(() => {
    if (orderError) {
      setErrorMessage("Failed to fetch orders.");
    } else if (orderData?.status) {
      const deliveredOrders = orderData.data
        .filter((order) =>
          order.vendors?.some(
            (vendor) =>
              vendor.order_status?.trim().toLowerCase() === "delivered",
          ),
        )
        .sort(
          (a, b) =>
            new Date(b.transaction_date_time) -
            new Date(a.transaction_date_time),
        );
      setDeliveredOrdersList(deliveredOrders);
    }
  }, [orderData, orderError]);

  useEffect(() => {
    if (siteSettingsError) {
    } else if (siteSettingsData && siteSettingsData["status"] === true) {
      setData(siteSettingsData["data"]);
    }
  }, [siteSettingsData, siteSettingsError]);

  const getActiveStep = (status) => {
    const statusMap = {
      pending: 0,
      confirmed: 1,
      processing: 2,
      picked: 3,
      shipped: 4,
      delivered: 5,
    };
    return statusMap[status?.toLowerCase()] ?? 0;
  };

  // const handleViewOrder = (vendor) => {
  //   setViewOrderData(vendor);
  //   setViewOrderModalOpen(true);
  // };

  const customerLat = useSelector((s) => s.user?.userLocation?.latitude);
  const customerLng = useSelector((s) => s.user?.userLocation?.longitude);

  const origin =
    customerLat && customerLng
      ? { lat: parseFloat(customerLat), lng: parseFloat(customerLng) }
      : null;

  const destination =
    viewOrderData?.vendorDetails?.latitude &&
    viewOrderData?.vendorDetails?.longitude
      ? {
          lat: parseFloat(viewOrderData?.vendorDetails?.latitude),
          lng: parseFloat(viewOrderData?.vendorDetails?.longitude),
        }
      : null;

  const [directions, setDirections] = useState(null);

  useEffect(() => {
    if (!origin || !destination || !window.google) return;

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") setDirections(result);
      },
    );
  }, [origin, destination]);

  const pathCoordinates = useMemo(() => {
    return origin && destination ? [destination, origin] : [];
  }, [origin, destination]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyA6CszWLFAJsbbDNtAtoXEWgWzqhU3pHB8",
  });

  // Handle opening invoice modal
  const handleOpenInvoiceModal = (vendor) => {
    setInvoiceData(vendor);
    setInvoiceModalOpen(true);
  };

  // PDF Generation Function
  const generateInvoicePDF = () => {
    if (!invoiceData) return;

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;

      // Calculate totals
      let subtotal = 0;
      invoiceData.items?.forEach((item) => {
        subtotal += parseFloat(item.sellingPrice) * item.quantity;
      });
      //const shipping = 50.0;
      const discount = 0.0;
      const platformFee = Number(data?.platform_fee || 0);
      const deliveryFee = Number(data?.delivery_partner_fee || 0);
      const gstPercent = Number(data?.gst_charge || 0);
      const gstAmount = (subtotal * gstPercent) / 100;
      const total = subtotal + platformFee + deliveryFee + gstAmount - discount;

      const currentDate = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("INVOICE", pageWidth / 2, 20, { align: "center" });

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Sthanick Web Services", pageWidth / 2, 30, { align: "center" });

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text("Payment Invoice", pageWidth / 2, 38, { align: "center" });

      // Add order details
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Order ID: #${invoiceData.cart_number}`, 20, 50);
      doc.text(`Date: ${currentDate}`, 20, 58);
      doc.text(`Total Items: ${invoiceData.items?.length || 0}`, 20, 66);

      doc.setLineWidth(0.5);
      doc.line(20, 75, pageWidth - 20, 75);

      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Order Items", 20, 85);

      // Table headers
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      let yPos = 95;
      doc.text("SKU", 20, yPos);
      doc.text("NAME", 40, yPos);
      doc.text("PRICE", 110, yPos);
      doc.text("QTY", 140, yPos);
      doc.text("DISC.", 160, yPos);
      doc.text("TOTAL", 180, yPos);

      doc.setLineWidth(0.3);
      doc.line(20, yPos + 2, pageWidth - 20, yPos + 2);

      // Add items
      doc.setFont("helvetica", "normal");
      yPos += 10;

      invoiceData.items?.forEach((item, index) => {
        const itemTotal = parseFloat(item.sellingPrice) * item.quantity;

        doc.text(`${index + 1}`, 20, yPos);

        // Wrap long product names
        const productName = item.product?.name || "N/A";
        if (productName.length > 25) {
          doc.text(productName.substring(0, 25) + "...", 40, yPos);
        } else {
          doc.text(productName, 40, yPos);
        }

        doc.text(`₹${item.sellingPrice}`, 110, yPos);
        doc.text(`x${item.quantity}`, 140, yPos);
        doc.text("0%", 160, yPos);
        doc.text(`₹${itemTotal.toFixed(2)}`, 180, yPos);

        yPos += 10;
      });

      yPos += 5;
      doc.line(20, yPos, pageWidth - 20, yPos);

      // Payment Summary
      yPos += 15;
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Payment Summary", 20, yPos);

      yPos += 12;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      doc.text("Subtotal:", 20, yPos);
      doc.text(`₹${subtotal.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });

      // yPos += 8;
      // doc.text("Shipping:", 20, yPos);
      // doc.text(`₹${shipping.toFixed(2)}`, pageWidth - 40, yPos, {
      //   align: "right",
      // });

      yPos += 8;
      doc.text("Platform Fee:", 20, yPos);
      doc.text(`₹${platformFee.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });

      yPos += 8;
      doc.text("Delivery Partner Fee:", 20, yPos);
      doc.text(`₹${deliveryFee.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });

      yPos += 8;

      doc.text(`GST (${gstPercent}%):`, 20, yPos);
      doc.text(`₹${gstAmount.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });

      yPos += 8;
      doc.text("Discount:", 20, yPos);
      doc.setTextColor(255, 0, 0);
      doc.text(`-₹${discount.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });
      doc.setTextColor(0, 0, 0);

      // Total line
      yPos += 12;
      doc.setLineWidth(0.5);
      doc.line(20, yPos - 4, pageWidth - 20, yPos - 4);

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Total:", 20, yPos);
      doc.setTextColor(25, 118, 210);
      doc.text(`₹${total.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });
      doc.setTextColor(0, 0, 0);

      // Vendor Information
      yPos += 20;
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Vendor Information", 20, yPos);

      yPos += 10;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      doc.text(`Vendor ID: ${invoiceData.vendor_id}`, 20, yPos);
      yPos += 7;
      doc.text(
        `Shop Name: ${invoiceData.vendorDetails?.shop_name || "N/A"}`,
        20,
        yPos,
      );
      yPos += 7;
      doc.text(
        `Phone: ${invoiceData.vendorDetails?.phone_number || "N/A"}`,
        20,
        yPos,
      );
      yPos += 7;
      doc.text(`Email: ${invoiceData.vendorDetails?.email || "N/A"}`, 20, yPos);
      yPos += 7;
      doc.text(
        `Location: ${invoiceData.vendorDetails?.location || "N/A"}`,
        20,
        yPos,
      );
      yPos += 7;
      doc.text(`State: ${invoiceData.vendorDetails?.state || "N/A"}`, 20, yPos);
      yPos += 7;
      doc.text(
        `Country: ${invoiceData.vendorDetails?.country || "N/A"}`,
        20,
        yPos,
      );

      // Add footer
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text("Thank you for your business!", pageWidth / 2, 280, {
        align: "center",
      });

      // Save the PDF
      doc.save(
        `Invoice_${invoiceData.cart_number}_${
          new Date().toISOString().split("T")[0]
        }.pdf`,
      );

      // Close modal after download
      setInvoiceModalOpen(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  //Rating Open Modal
  const handleOpenRatingModal = (vendor, item) => {
    setRatingModalOpen(true);
    setSelectedVendor(vendor);
    setSelectedProduct(item);
    setRatingErrorMsg("");
    setRatingSuccessMsg("");
  };
  //Rating Close Modal
  const handleCloseRatingModal = () => {
    setRatingModalOpen(false);
    setRatingSubmitted(false);
    setRatingValue(null);
    setRemarks("");
    setRatingErrorMsg("");
    setRatingSuccessMsg("");
    setSelectedProduct(null);
    setSelectedVendor(null);
  };
  //Submit Rating
  const handleSubmitRating = () => {
    setRatingErrorMsg("");
    setRatingSuccessMsg("");

    if (!ratingValue) {
      setRatingErrorMsg("Please select rating");
      return;
    }

    let vendorType;
    if (selectedProduct?.vlisting_id) {
      vendorType = "listing";
    } else if (selectedProduct?.product_id) {
      vendorType = "regular";
    } else {
      vendorType = "regular";
    }
    const payload = {
      vendor_product_id: selectedProduct?.vendor_product_id,
      vendor_id: selectedVendor?.vendor_id,
      customer_id: customerId,
      rating: ratingValue.toString(),
      description: remarks,
      vendor_type: vendorType,
    };
    if (vendorType === "listing") {
      payload.product_id = selectedProduct?.vlisting_id;
    } else {
      payload.product_id = selectedProduct?.product_id;
    }
    fetchRatingOrder(payload);
  };

  useEffect(() => {
    if (ratingOrderData?.status === true) {
      setRatingSuccessMsg(
        ratingOrderData?.message || "Thank you for your review! ⭐",
      );
      if (ratingOrderData?.data?.status === 1) {
        setRatingSubmitted(true);
      }
      setTimeout(() => {
        setRatingModalOpen(false);
        setRatingValue(null);
        setRemarks("");
        setSelectedProduct(null);
        setSelectedVendor(null);
        setRatingSuccessMsg("");
        setRatingErrorMsg("");
        setRatingSubmitted(false);
      }, 2000);
      setRatingSubmitted(true);
    } else if (ratingOrderData?.status === false) {
      setRatingErrorMsg(
        ratingOrderData?.message ||
          "Failed to submit review. Please try again.",
      );
    }

    if (ratingOrderError) {
      setRatingErrorMsg(
        ratingOrderError?.message ||
          "Failed to submit review. Please try again.",
      );
    }
  }, [ratingOrderData, ratingOrderError]);

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        Delivered Orders
      </Typography>

      <Card>
        <Box p={2}>
          {errorMessage && (
            <Typography color="error">{errorMessage}</Typography>
          )}

          {deliveredOrdersList.length === 0 && !errorMessage && (
            <Typography>No delivered orders found.</Typography>
          )}

          {deliveredOrdersList.map((order) => (
            <OrderCard
              key={order.cart_id}
              order={order}
              handleOpenInvoiceModal={handleOpenInvoiceModal}
              handleOpenRatingModal={handleOpenRatingModal}
            />
          ))}
        </Box>
      </Card>

      <Dialog open={trackOderModal} onClose={() => setTrackOderModal(false)}>
        <DialogTitle>Track Delivery Status</DialogTitle>
        <DialogContent>
          <Stepper
            activeStep={getActiveStep(trackingOrder?.order_status)}
            alternativeLabel
          >
            {[
              "Pending",
              "Confirmed",
              "Processing",
              "Picked",
              "Shipped",
              "Delivered",
            ].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackOderModal(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- View Order Modal ---------------- */}
      <Dialog
        open={isViewOrderModalOpen}
        onClose={() => setViewOrderModalOpen(false)}
        fullWidth
      >
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent dividers>
          {viewOrderData && (
            <>
              <Typography variant="h6">Vendor Details</Typography>
              <Typography>
                Shop: {viewOrderData.vendorDetails?.shop_name}
              </Typography>
              <Typography>
                Phone: {viewOrderData.vendorDetails?.phone_number}
              </Typography>

              {isLoaded && pathCoordinates.length === 2 && (
                <Box
                  sx={{
                    height: 300,
                    borderRadius: 2,
                    overflow: "hidden",
                    mt: 2,
                  }}
                >
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    zoom={13}
                  >
                    {directions && (
                      <DirectionsRenderer directions={directions} />
                    )}
                    <MarkerF position={pathCoordinates[0]} />
                    <MarkerF position={pathCoordinates[1]} />
                  </GoogleMap>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewOrderModalOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Invoice Modal */}
      <Dialog
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "bold", fontSize: "1.5rem" }}
        >
          Invoice Details
        </DialogTitle>
        <DialogContent dividers>
          {invoiceData && (
            <>
              <Box mb={3}>
                <Typography variant="body1" gutterBottom>
                  <strong>Order ID:</strong> #{invoiceData.cart_number}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Total Items:</strong> {invoiceData.items?.length || 0}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Date:</strong>{" "}
                  {new Date().toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Order Items
              </Typography>

              <Box sx={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: "10px",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        SKU
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        NAME
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        PRICE
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        QTY
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        DISC.
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        TOTAL
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.items?.map((item, index) => (
                      <tr key={item.cart_item_id}>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          {item.product?.name}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          ₹{item.sellingPrice}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          x{item.quantity}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                            color: "red",
                          }}
                        >
                          0%
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          ₹
                          {(
                            parseFloat(item.sellingPrice) * item.quantity
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Payment Summary
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Subtotal:</Typography>
                  <Typography>
                    ₹
                    {invoiceData.items
                      ?.reduce(
                        (sum, item) =>
                          sum + parseFloat(item.sellingPrice) * item.quantity,
                        0,
                      )
                      .toFixed(2)}
                  </Typography>
                </Box>
                {/* <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Shipping:</Typography>
                  <Typography>₹50.00</Typography>
                </Box> */}
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Platform Fee:</Typography>
                  <Typography>
                    ₹{Number(data?.platform_fee || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Delivery Partner Fee:</Typography>
                  <Typography>
                    ₹{Number(data?.delivery_partner_fee || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>
                    GST Charge({Number(data?.gst_charge || 0)}%):
                  </Typography>
                  <Typography>
                    {/* ₹{Number(data?.gst_charge || 0).toFixed(2)} */}₹
                    {(
                      ((invoiceData.items?.reduce(
                        (sum, item) =>
                          sum + Number(item.sellingPrice) * item.quantity,
                        0,
                      ) || 0) *
                        Number(data?.gst_charge || 0)) /
                      100
                    ).toFixed(2)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Discount:</Typography>
                  <Typography sx={{ color: "red" }}>-₹0.00</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" fontWeight="bold">
                    Total:
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#1976d2" }}
                  >
                    ₹
                    {(
                      (invoiceData.items?.reduce(
                        (sum, item) =>
                          sum + Number(item.sellingPrice) * item.quantity,
                        0,
                      ) || 0) +
                      Number(data?.platform_fee || 0) +
                      Number(data?.delivery_partner_fee || 0) +
                      ((invoiceData.items?.reduce(
                        (sum, item) =>
                          sum + Number(item.sellingPrice) * item.quantity,
                        0,
                      ) || 0) *
                        Number(data?.gst_charge || 0)) /
                        100
                    ).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3, pt: 2 }}>
          <Button
            onClick={generateInvoicePDF}
            variant="contained"
            sx={{
              backgroundColor: "#00897B",
              textTransform: "none",
              borderRadius: 2,
              px: 4,
              "&:hover": {
                backgroundColor: "#00796B",
              },
            }}
          >
            Download Invoice
          </Button>
          <Button
            onClick={() => setInvoiceModalOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 4,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {/* Rating Modal */}
      <Dialog
        open={ratingModalOpen}
        onClose={handleCloseRatingModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "bold", fontSize: "1.5rem" }}
        >
          Rate Your Order
        </DialogTitle>
        <DialogContent dividers>
          <Box>
            {/* Error Message */}
            {ratingErrorMsg && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {ratingErrorMsg}
              </Alert>
            )}

            {/* Success Message */}
            {ratingSuccessMsg && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {ratingSuccessMsg}
              </Alert>
            )}

            {/* Product Info */}
            {selectedProduct && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Rating for:
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedProduct?.product?.name}
                </Typography>
              </Box>
            )}

            <Typography variant="body1" gutterBottom fontWeight={500}>
              Your Rating:
            </Typography>
            <Rating
              value={ratingValue}
              onChange={(event, newValue) => setRatingValue(newValue)}
              precision={0.5}
              size="large"
              sx={{ mb: 3 }}
            />

            <Typography variant="body1" gutterBottom fontWeight={500}>
              Remarks (Optional):
            </Typography>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows="4"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                fontSize: "1rem",
                fontFamily: "inherit",
                resize: "vertical",
              }}
              placeholder="Share your experience with this product..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, p: 3 }}>
          <Button
            onClick={handleSubmitRating}
            disabled={ratingOrderLoading || ratingSubmitted}
            variant="contained"
            sx={{
              backgroundColor: "#FF9800",
              textTransform: "none",
              borderRadius: 2,
              px: 4,
              py: 1,
              "&:hover": {
                backgroundColor: "#F57C00",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {ratingOrderLoading ? "Submitting..." : "Submit Rating"}
          </Button>
          <Button
            onClick={handleCloseRatingModal}
            variant="outlined"
            disabled={ratingOrderLoading}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 4,
              py: 1,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Delivered;
